import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";
import { Button } from "./button";
import { Skeleton } from "./skeleton";

/** A skeleton shaped roughly like the content it replaces, not a spinner. */
export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2" data-testid="loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export interface NotBuiltYetProps extends HTMLAttributes<HTMLDivElement> {
  /** One sentence: what this destination is for, and why it is not here yet. */
  summary: ReactNode;
  /** What a person will be able to do here. Concrete, not a roadmap. */
  willDo?: readonly string[];
  /**
   * The heading.
   *
   * Defaulted, because the sentence that matters is `summary` and every screen
   * that wrote its own version of this heading wrote a slightly different one.
   *
   * The default is the shipped sentence, word for word. It was shortened to
   * "Not built yet" during this redesign on the strength of an artboard, and
   * `system/rationale.md` — which names every deliberate copy change in it —
   * does not name that one. Shipped copy stays unless the rationale authorises
   * the change, so it is back. The prop is the right mechanism and stays.
   */
  title?: string;
  /**
   * The line that says this is not a fault.
   *
   * Defaulted to the console's, which names the three things a person assumes
   * when a screen has little on it. A second shell says "shell" where this says
   * "console" and passes its own.
   */
  reassurance?: ReactNode;
  /** Where to go instead, today. */
  children?: ReactNode;
}

/**
 * A destination the architecture names and nobody has built yet.
 *
 * Deliberately not `EmptyState`, and the distinction is the whole point of the
 * component. "Nothing here yet" is a statement about *your* data — no players
 * have signed in, no rules have been written — and it implies that doing the
 * obvious thing would fill the screen. Saying that about a feature which does
 * not exist is a lie that costs a support ticket: someone reads "no offers
 * yet", goes looking for the button that adds one, and concludes the console is
 * broken when they cannot find it.
 *
 * So the shape is shared with the rest of the console — same surface, same
 * tokens, same voice — and the sentence is different. It says the screen is not
 * built, says what it will do when it is, and hands over somewhere real to go
 * instead. A person who lands here by curiosity leaves knowing one more true
 * thing about the product rather than one false thing about their game.
 *
 * A card, and the containment rule's one exception: the boundary around a
 * genuinely single-purpose screen says *this is all there is*, where an
 * unbounded sentence on an empty page reads as a page that failed to load.
 *
 * What it must never be: a chart of invented numbers, a spinner that never
 * resolves, or an error. All three are how an unfinished console teaches people
 * to distrust the finished parts of it.
 */
export function NotBuiltYet({
  className,
  summary,
  willDo,
  title = "This screen is not built yet",
  reassurance = "Nothing is loading and nothing has gone wrong. This part of the console has not been written yet, and there is nothing here for you to fix.",
  children,
  ...props
}: NotBuiltYetProps) {
  return (
    <div
      data-testid="not-built"
      className={cn(
        "flex w-full max-w-2xl flex-col items-center gap-3 rounded-[var(--radius-card)]",
        "border border-rule bg-surface px-7 py-8 text-center",
        className,
      )}
      {...props}
    >
      <span className="flex size-13 items-center justify-center rounded-full border border-rule-strong bg-surface-2 text-ink-2">
        <PendingMark />
      </span>
      <h1 className="text-display font-semibold text-balance text-ink">{title}</h1>
      <p className="max-w-prose text-prose text-ink-2">{summary}</p>
      {willDo && willDo.length > 0 ? (
        <ul className="flex max-w-prose flex-col gap-2 text-left">
          {willDo.map((line) => (
            <li key={line} className="flex gap-2.5 text-ui text-ink-2">
              <span
                aria-hidden="true"
                className="mt-2 size-1 shrink-0 rounded-full bg-rule-strong"
              />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {/*
        Said plainly, because the three things people assume when a screen has
        little on it are that it is still loading, that it failed, or that their
        game is empty. None of those is true here.
      */}
      <p className="max-w-prose text-prose text-ink">{reassurance}</p>
      {children ? (
        <div className="mt-2 flex flex-col items-center gap-2">{children}</div>
      ) : null}
    </div>
  );
}

export interface GoInsteadProps extends HTMLAttributes<HTMLParagraphElement> {
  /**
   * The destination, as whatever the app's router calls a link.
   *
   * A node rather than a `to` and a `label` because this package has no router
   * and must not gain one — `@tanstack/react-router`'s `Link` needs the app's
   * own route tree to typecheck a path. The accent, the weight and the
   * underline are applied here, so every way out looks the same in both shells.
   */
  link: ReactNode;
  /** What that destination answers. */
  children: ReactNode;
}

/**
 * One place to go instead, with the reason it is the right one.
 *
 * A bare "go back" is not a way onward. Each of these names a destination that
 * exists today and says what it answers, so the sentence survives being read by
 * somebody who has never seen either screen.
 */
export function GoInstead({ className, link, children, ...props }: GoInsteadProps) {
  return (
    <p className={cn("text-prose text-ink-2", className)} {...props}>
      <span className="font-medium text-accent-text underline underline-offset-2">{link}</span>{" "}
      — {children}
    </p>
  );
}

export interface SignedOutProps extends HTMLAttributes<HTMLDivElement> {
  /** The product, as the door should name it. */
  product: string;
  /** One line under it. What signing in gets you, not what signing in is. */
  tagline: string;
  /** Starts a sign-in. */
  onSignIn: () => void;
  /** Starts an enrolment. Absent on a shell nobody enrols themselves into. */
  onCreateAccount?: () => void;
  /** What the two buttons say, when "Sign in" and "Create an account" are not it. */
  signInLabel?: string;
  createLabel?: string;
  /**
   * The lockup in the strip across the top — a mark and a word, usually.
   *
   * A prop rather than a drawing, because the mark lives in each app rather
   * than in this package, and the staff shell's is not the console's.
   */
  brand?: ReactNode;
  /**
   * The small line above the heading, beside a dot.
   *
   * Say what state this is, never that something broke. This card is reached
   * both when a hand-off did not land and when somebody signed out and simply
   * came back, and only one of those is a failure — see the note on the
   * component.
   */
  status?: ReactNode;
  /**
   * Why the reader is looking at a door rather than being taken through one.
   *
   * `meta`/`ink-3`, under the buttons. The console's hand-off is claimed once
   * per tab, so somebody reaching this card needs to be told that nothing is
   * loading and nothing will happen again on its own.
   */
  note?: ReactNode;
  /**
   * The hand-off is in flight.
   *
   * Says nothing louder than a line of text: this is on screen for a few
   * hundred milliseconds, a spinner would flash, and a control that appears and
   * vanishes is one somebody reaches for and misses.
   */
  handingOff?: boolean;
  /** What the quiet state says while the hand-off is in flight. */
  handingOffLabel?: string;
}

/**
 * The door for somebody who is not signed in.
 *
 * In the console it is reached only when the automatic hand-off failed. The
 * ordinary path never renders the card: a signed-out visitor goes straight to
 * control's sign-in screen, which is the one designed for the job and the one
 * all four clients share. Two screens saying "sign in" is one too many, and the
 * second one was a click in front of the first.
 *
 * So this is a fallback and it says so. Its job is to be a way forward when
 * redirecting has already not worked, which is exactly when an automatic second
 * attempt would be a loop.
 *
 * # It does not say the sign-in broke
 *
 * The board this is drawn from headed it "We could not send you to sign-in",
 * over a critical dot reading SIGN-IN HAND-OFF FAILED. That is true of one of
 * the two ways in here and false of the other: the hand-off is claimed once per
 * tab, so somebody who signs out and comes back inside the window reaches this
 * card having had nothing go wrong at all. The card takes the board's shape —
 * the strip, the panel, the status line, two actions side by side — and leaves
 * the claim to the caller, which is the only place that knows.
 */
export function SignedOut({
  className,
  product,
  tagline,
  onSignIn,
  onCreateAccount,
  signInLabel = "Sign in",
  createLabel = "Create an account",
  brand,
  status,
  note,
  handingOff = false,
  handingOffLabel = "Taking you to sign in…",
  ...props
}: SignedOutProps) {
  if (handingOff) {
    return (
      <div className={cn("grid min-h-dvh place-items-center px-6", className)} {...props}>
        <p className="text-prose text-ink-3">{handingOffLabel}</p>
      </div>
    );
  }
  return (
    <div className={cn("flex min-h-dvh flex-col bg-ground", className)} {...props}>
      {brand ? (
        <div className="flex h-[var(--header-h)] shrink-0 items-center border-b border-rule px-5">
          {brand}
        </div>
      ) : null}
      {/* Flex, not a centred grid: a grid item is content-sized, so a max-width
          on the panel resolves against the panel itself and stops constraining
          it — which put 470px of card on a 390px screen. */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="flex w-full max-w-[470px] flex-col gap-4 rounded-[var(--radius-xl)] border border-rule bg-surface p-6">
          {status ? (
            <p className="flex items-center gap-2.5 text-micro font-semibold tracking-[0.1em] text-ink-3 uppercase">
              <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-ink-3" />
              {status}
            </p>
          ) : null}
          <h1 className="text-display font-semibold text-ink">{product}</h1>
          <p className="text-prose text-ink-2">{tagline}</p>
          {/* Side by side and equal, because neither is the obvious one: which
              of them you want depends on whether you already have an account,
              and the card has no way to know. */}
          <div className="flex flex-wrap gap-2.5">
            <Button className="flex-1" onClick={onSignIn}>
              {signInLabel}
            </Button>
            {onCreateAccount ? (
              <Button className="flex-1" variant="secondary" onClick={onCreateAccount}>
                {createLabel}
              </Button>
            ) : null}
          </div>
          {note ? <p className="text-meta text-ink-3">{note}</p> : null}
        </div>
      </div>
    </div>
  );
}

/** 16px stroke mark on a 24px grid, at 1.8, in `currentColor` — house rules §8. */
function PendingMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
      <path d="M8.5 12h7" />
    </svg>
  );
}
